import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Container,
  Row,
  Col,
  Card,
  Form,
  FloatingLabel,
  Button,
  Spinner,
} from "react-bootstrap";

import { MapPin } from "react-feather";

import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

import { useNotification } from "../../components/notification";
import PhoneVerification from "../../components/phoneVerificationForm";

import { useAuth } from "../../contexts/authContext";

import apiService from "../../services/apiService";
import ManageAddress from "../address/_manage";

// Define validation schema using yup
const schema = yup.object().shape({
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  phone: yup
    .string()
    .required("Phone is required")
    .matches(/^\d{10}$/, "Phone must be 10 digits"),
  gstNumber: yup.string().notRequired(), // Optional field
});

const Profile = () => {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
    },
  });

  const { user } = useAuth();
  const { showNotification } = useNotification();
  const didFetch = useRef(false);

  const [addresses, setAddresses] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPhoneVerified, setIsPhoneVerified] = useState(user?.phoneVerified || false);
  const [phone, setPhone] = useState(user?.phone || "");

  // Fetch user profile
  const fetchUserProfile = useCallback(async () => {
    try {
      const response = await apiService.get(`/user-profile`);
      reset(response); // Pre-populate the form with fetched data
      setPhone(response?.phone);
      setIsPhoneVerified(response?.phoneVerified);
    } catch (error) {
      showNotification("danger", error || "Failed to get profile.", 5000);
    }
  }, [reset, showNotification]);

  const fetchAddresses = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await apiService.get(`/addresses/user/${user.id}`);
      setAddresses(response);
      setTotalRecords(response ? response.length : 0);
    } catch (error) {
      showNotification("danger", error || "Failed to get address.", 5000);
    } finally {
      setIsLoading(false);
    }
  }, [user, showNotification]);

  useEffect(() => {
    if (didFetch.current) return; // Prevent second call
    didFetch.current = true;

    // Fetch user profile and addresses on initial load
    fetchUserProfile();
    fetchAddresses();
  }, [fetchUserProfile, fetchAddresses]);

  // Handle form submission
  const onSubmit = async (data) => {
      // if (!isPhoneVerified) {
      //   showNotification("danger", "Please verify your phone number before saving.", 5000);
      //   return;
      // }

    setIsSubmitting(true);
    try {
      await apiService.put(`/user-profile`, data);
      showNotification("success", "Profile updated!", 5000);
    } catch (error) {
      showNotification("danger", error || "Failed to get profile.", 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container fluid className="p-0">
      <Card className="p-4 rounded-4">
        <Card.Body>
          <h1 className="mb-1 fs-5">Profile</h1>
          <p className="mb-0 text-light-emphasis fw-light">
            Update the profile details below.
          </p>
          <hr className="mt-3 mb-4" />
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col sm={6} lg={4}>
                <FloatingLabel
                  controlId="txtFirstName"
                  label="First Name"
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder="First Name"
                    {...register("firstName")}
                    isInvalid={!!errors.firstName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.firstName?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col sm={6} lg={4}>
                <FloatingLabel
                  controlId="txtLastName"
                  label="Last Name"
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder="Last Name"
                    {...register("lastName")}
                    isInvalid={!!errors.lastName}
                  />
                  <Form.Control.Feedback type="invalid">
                    {errors.lastName?.message}
                  </Form.Control.Feedback>
                </FloatingLabel>
              </Col>

              <Col sm={6} lg={4}>
                <FloatingLabel
                  controlId="txtGstNumber"
                  label="GST Number (Optional)"
                  className="mb-3"
                >
                  <Form.Control
                    type="text"
                    placeholder="GST Number"
                    {...register("gstNumber")}
                  />
                </FloatingLabel>
              </Col>

              <Col sm={6} lg={6}>
                <PhoneVerification
                  value={getValues("phone")}
                  onChange={(val) => {
                    setValue("phone", val);
                    if (val !== user?.phone) {
                      setIsPhoneVerified(false);
                      //user.phoneVerified = false;
                    }
                    // else if (val === user?.phone) {
                    //   setIsPhoneVerified(true);
                    //   user.phoneVerified = true;
                    // }
                  }}
                  onVerified={setIsPhoneVerified}
                  disabled={isSubmitting}
                  existingPhone={user?.phone || ""}
                  isExistingVerified={user?.phoneVerified}
                />
                <input type="hidden" {...register("phone")} />
                {errors.phone && (
                  <div className="text-danger small mt-1">{errors.phone.message}</div>
                )}
              </Col>
            </Row>
            <Button
              variant="primary btn-main"
              type="submit"
              disabled={isSubmitting || !isPhoneVerified}
            >
              {isSubmitting ? (
                <Spinner animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ) : (
                "Update Profile"
              )}
            </Button>
          </Form>
          <div className="d-flex align-items-center justify-content-between mt-4">
            <h2 className="mb-0 fs-6">Manage Addresses</h2>
            <Button
              as={Link}
              to={`/address/create/${user.id}`}
              variant="primary"
              className="btn-main mb-3 mb-sm-0"
            >
              <MapPin size={14} className="me-1 align-middle" />
              Add New Address
            </Button>
          </div>
          <p className="mb-0 text-light-emphasis fw-light">
            Ensure addresses are linked to your account.
          </p>
          <hr className="my-3" />
          <ManageAddress
            addresses={addresses}
            setAddresses={setAddresses}
            totalRecords={totalRecords}
            showNotification={showNotification}
            isLoading={isLoading}
            isAdminView={false}
            userRole={user?.role}
          />
        </Card.Body>
      </Card>
    </Container>
  );
};

export default Profile;
